import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguageStore, useAuthStore, useWishlistStore, useQuoteStore, useProjectStore, getLocalized } from '../store';
import { useTranslation } from '../i18n/translations';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';
import { ProductGallery } from '../components/product/ProductGallery';
import { ProductInfo } from '../components/product/ProductInfo';
import { ProductSpecs } from '../components/product/ProductSpecs';
import { RelatedProducts } from '../components/product/RelatedProducts';
import { FileText, Box } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Универсальный безопасный сериализатор строк
 */
const safeString = (val, lang = 'en') => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string' || typeof val === 'number') return String(val);
  if (Array.isArray(val)) {
    return val.map((item) => safeString(item, lang)).filter(Boolean).join(', ');
  }
  if (typeof val === 'object') {
    if (val[lang]) return safeString(val[lang], lang);
    if (val.en) return safeString(val.en, lang);
    if (val.ru) return safeString(val.ru, lang);
    if (val.nl) return safeString(val.nl, lang);
    if (val.ro) return safeString(val.ro, lang);
    
    if (val.width || val.height || val.depth || val.w || val.h || val.d) {
      const w = val.width || val.w || '';
      const h = val.height || val.h || '';
      const d = val.depth || val.d || '';
      return [w, h, d].filter(Boolean).join(' × ');
    }
    
    const firstStr = Object.values(val).find((v) => typeof v === 'string');
    if (firstStr) return firstStr;
    return '';
  }
  return String(val);
};

/* STREAMING_CHUNK: Initializing state hooks and API fetching logic... */
export const ProductDetailPage = () => {
  const { id } = useParams();
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  
  const userFromHook = useAuthStore((s) => s.user);
  const tokenFromHook = useAuthStore((s) => s.token);
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlistStore();
  const { addItem, items: quoteItems } = useQuoteStore();
  const { projects, activeProjectId, createProject, addItemToProject } = useProjectStore();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hydrated, setHydrated] = useState(false);
  const [quantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const [currentSelection, setCurrentSelection] = useState({ materials: null, finishes: null });

  const pageDict = {
    toastWishlistIn: { en: 'Please sign in to add items to your wishlist', ru: 'Пожалуйста, войдите, чтобы добавить товар в избранное', nl: 'Meld u aan om items aan uw verlanglijst toe te voegen', ro: 'Vă rugăm să vă autentificați pentru a adăuga produse în favorite' },
    toastWishlistAdd: { en: 'Added to wishlist', ru: 'Добавлено в избранное', nl: 'Toegevoegd aan verlanglijst', ro: 'Adăugat la favorite' },
    toastWishlistRem: { en: 'Removed from wishlist', ru: 'Удалено из избранного', nl: 'Verwijderd van verlanglijst', ro: 'Eliminat din favorite' },
    toastQuoteAdd: { en: 'Added to specification request', ru: 'Добавлено в запрос спецификации', nl: 'Toegevoegd aan projectspecificatie', ro: 'Adăugat la cererea de specificație' },
    toastProjectErr: { en: 'Could not add to project. Please try again.', ru: 'Не удалось добавить в проект. Пожалуйста, попробуйте еще раз.', nl: 'Kon niet aan project worden toegevoegd. Probeer het opnieuw.', ro: 'Nu s-a putut adăuga la proiect. Vă rugăm să încercați din nou.' },
    toastProjectAdd: { en: 'Added to project specification', ru: 'Добавлено в спецификацию проекта', nl: 'Toegevoegd aan projectspecificatie', ro: 'Adăugat la specificația проекта' },
    notFound: { en: 'Product not found', ru: 'Товар не найден', nl: 'Product niet gevonden', ro: 'Produsul nu a fost găsit' },
    defaultProjectName: { en: 'My Project', ru: 'Мой проект', nl: 'Mijn project', ro: 'Proiectul meu' },
    techSheetFallback: { en: 'Tech Sheet', ru: 'Техническая карта', nl: 'Technische fiche', ro: 'Fișă tehnică' },
    model3dFallback: { en: '3D Model Asset', ru: '3D-Модель изделия', nl: '3D-model', ro: 'Model 3D' },
    cadLabel: { en: 'CAD / 3D File', ru: 'Файл CAD / 3D', nl: 'Файл CAD / 3D', ro: 'Fișier CAD / 3D' }
  };

  useEffect(() => {
    const timer = setTimeout(() => setHydrated(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const user = userFromHook || (hydrated ? useAuthStore.getState().user : null);
  const token = tokenFromHook || (hydrated ? useAuthStore.getState().token : null);
  const isB2BApproved = !!(user?.is_b2b && user?.is_approved) || user?.role === 'admin';

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setError(null);
      try {
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const res = await fetch(`${API}/api/products/${id}`, { headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}: Product not found`);
        const data = await res.json();

        const rawProduct = data?.product || data;
        if (!rawProduct || typeof rawProduct !== 'object') {
          throw new Error('Invalid product data structure');
        }
        setProduct(rawProduct);

        if (rawProduct.category) {
          try {
            const relatedRes = await fetch(`${API}/api/products?category=${rawProduct.category}&limit=4`, { headers });
            if (relatedRes.ok) {
              const relatedData = await relatedRes.json();
              const items = Array.isArray(relatedData) ? relatedData : (relatedData?.products || []);
              setRelatedProducts(items.filter((p) => (p?.id || p?._id) !== id));
            }
          } catch (e) {
            console.warn('Could not fetch related products:', e);
          }
        }
      } catch (err) {
        console.error('Failed to fetch product details:', err);
        setError(err.message || 'Error loading product');
      } finally {
        setLoading(false);
      }
    };

    if (hydrated && id) fetchProduct();
  }, [id, hydrated, token]);

  useEffect(() => {
    if (product) {
      setCurrentSelection({
        materials: safeString(getLocalized(product.material, language), language),
        finishes: safeString(getLocalized(product.finish, language), language)
      });
    }
  }, [product, language]);

  /* STREAMING_CHUNK: Handlers for wishlist, quote, project and PDF actions... */
  const handleWishlist = async () => {
    if (!product?.id) return;
    if (!user) {
      toast.error(safeString(pageDict.toastWishlistIn[language] || pageDict.toastWishlistIn.en, language));
      return;
    }
    const inWish = typeof isInWishlist === 'function' ? isInWishlist(product.id) : false;
    if (inWish) {
      if (typeof removeFromWishlist === 'function') await removeFromWishlist(product.id);
      toast.success(safeString(pageDict.toastWishlistRem[language] || pageDict.toastWishlistRem.en, language));
    } else {
      if (typeof addToWishlist === 'function') await addToWishlist(product.id);
      toast.success(safeString(pageDict.toastWishlistAdd[language] || pageDict.toastWishlistAdd.en, language));
    }
  };

  const handleAddToQuote = async () => {
    if (!product) return;
    const tailoredProduct = {
      ...product,
      selected_custom_material: currentSelection.materials,
      selected_custom_finish: currentSelection.finishes
    };
    if (typeof addItem === 'function') addItem(tailoredProduct, quantity);

    if (isB2BApproved) {
      let pid = activeProjectId || projects?.[0]?.id;
      if (!pid && typeof createProject === 'function') pid = await createProject(safeString(pageDict.defaultProjectName[language], language) || 'My Project');
      if (pid && typeof addItemToProject === 'function') {
        addItemToProject(pid, { 
          product_id: product.id, 
          sku: safeString(product.sku, language), 
          name: safeString(getLocalized(product.name, language), language),
          custom_material: currentSelection.materials,
          custom_finish: currentSelection.finishes
        });
      }
    }
    toast.success(safeString(pageDict.toastQuoteAdd[language] || pageDict.toastQuoteAdd.en, language));
  };

  const handleAddToProject = async () => {
    if (!product) return;
    let pid = activeProjectId || projects?.[0]?.id;
    if (!pid && typeof createProject === 'function') pid = await createProject(safeString(pageDict.defaultProjectName[language], language) || 'My Project');
    if (!pid) {
      toast.error(safeString(pageDict.toastProjectErr[language] || pageDict.toastProjectErr.en, language));
      return;
    }
    
    if (typeof addItemToProject === 'function') {
      addItemToProject(pid, { 
        product_id: product.id, 
        sku: safeString(product.sku, language), 
        name: safeString(getLocalized(product.name, language), language),
        custom_material: currentSelection.materials,
        custom_finish: currentSelection.finishes
      });
    }

    const tailoredProduct = {
      ...product,
      selected_custom_material: currentSelection.materials,
      selected_custom_finish: currentSelection.finishes
    };
    if (typeof addItem === 'function') addItem(tailoredProduct, quantity);

    toast.success(safeString(pageDict.toastProjectAdd[language] || pageDict.toastProjectAdd.en, language));
  };

  const handleGeneratePDF = async () => {
    if (!product?.id) return;
    setIsGeneratingPdf(true);
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const matParam = currentSelection.materials ? `&selected_material=${encodeURIComponent(currentSelection.materials)}` : '';
      const finParam = currentSelection.finishes ? `&selected_finish=${encodeURIComponent(currentSelection.finishes)}` : '';
      
      const res = await fetch(`${API}/api/products/${product.id}/datasheet?lang=${language}${matParam}${finParam}`, { headers });
      if (!res.ok) throw new Error('Server returned error during PDF compilation');

      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${safeString(getLocalized(product.name, language), language) || 'Product'}_Specification.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(language === 'ru' ? 'PDF спецификация успешно создана' : 'PDF specification downloaded');
    } catch (err) {
      console.error(err);
      toast.error(language === 'ru' ? 'Не удалось сгенерировать PDF спецификацию' : 'Failed to generate PDF datasheet');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  /* STREAMING_CHUNK: Sanitizing product data for child components... */
  const cleanedProductForInfo = useMemo(() => {
    if (!product) return null;
    return {
      ...product,
      sku: safeString(product.sku, language),
      name: safeString(getLocalized(product.name, language), language),
      description: safeString(getLocalized(product.description, language), language),
      material: product.material,
      finish: product.finish
    };
  }, [product, language]);

  const cleanedProductForSpecs = useMemo(() => {
    if (!product) return null;
    const matVal = safeString(getLocalized(product.material, language), language);
    const finVal = safeString(getLocalized(product.finish, language), language);

    return {
      ...product,
      sku: safeString(product.sku, language),
      name: safeString(getLocalized(product.name, language), language),
      category: safeString(getLocalized(product.category, language) || product.category, language),
      material: product.material,
      finish: product.finish,
      dimensions: safeString(product.dimensions, language),
      materials: Array.isArray(product.materials) && product.materials.length > 0 
        ? product.materials.map((m) => safeString(getLocalized(m, language), language)) 
        : (matVal ? [matVal] : []),
      finishes: Array.isArray(product.finishes) && product.finishes.length > 0 
        ? product.finishes.map((f) => safeString(getLocalized(f, language), language)) 
        : (finVal ? [finVal] : [])
    };
  }, [product, language]);

  const cleanedRelatedProducts = useMemo(() => {
    if (!Array.isArray(relatedProducts)) return [];
    return relatedProducts.map((p) => {
      const pName = safeString(getLocalized(p?.name, language) || p?.name, language);
      const pCat = safeString(getLocalized(p?.category, language) || p?.category, language);
      const pDesc = safeString(getLocalized(p?.description, language) || p?.description, language);
      const pImg = typeof p?.images?.[0]?.url === 'string' 
        ? p.images[0].url 
        : (typeof p?.images?.[0] === 'string' ? p.images[0] : (p?.image || ''));

      return {
        ...p,
        id: p?.id || p?._id,
        name: pName,
        category: pCat,
        description: pDesc,
        image: pImg,
        images: Array.isArray(p?.images) ? p.images : []
      };
    });
  }, [relatedProducts, language]);

  const cleanedCategoryLabels = useMemo(() => {
    if (!t?.categories || typeof t.categories !== 'object') return {};
    const result = {};
    Object.keys(t.categories).forEach((key) => {
      result[key] = safeString(getLocalized(t.categories[key], language) || t.categories[key], language);
    });
    return result;
  }, [t?.categories, language]);

  const isInQuote = useMemo(() => {
    if (!product?.id || !Array.isArray(quoteItems)) return false;
    return quoteItems.some((i) => {
      const itemId = i?.product?.id || i?.id || i?.product_id;
      return itemId === product.id;
    });
  }, [quoteItems, product]);

  const isWishlisted = useMemo(() => {
    if (!product?.id || typeof isInWishlist !== 'function') return false;
    return isInWishlist(product.id);
  }, [product, isInWishlist]);

  if (loading || !hydrated) {
    return (
      <div className="min-h-screen">
        <div className="container-custom py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="aspect-square bg-muted animate-pulse" />
            <div className="space-y-4">
              <div className="h-8 bg-muted w-3/4 animate-pulse" />
              <div className="h-4 bg-muted w-1/2 animate-pulse" />
              <div className="h-32 bg-muted animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center select-none">
        <p className="text-muted-foreground text-lg mb-6">{safeString(pageDict.notFound[language] || pageDict.notFound.en, language)}</p>
        <Link to={`/${language}/products`} className="btn-primary text-xs uppercase tracking-widest px-6 py-3 bg-black text-white hover:bg-neutral-800 transition-colors">
          {language === 'ru' ? 'Вернуться в каталог' : 'Back to Catalogue'}
        </Link>
      </div>
    );
  }

  /* STREAMING_CHUNK: Rendering complete JSX page layout... */
  const productName = safeString(getLocalized(product.name, language), language) || 'Product';
  const productDesc = safeString(getLocalized(product.description, language), language) || '';
  const productCat = safeString(product.category, language) || '';

  const infoLabels = { 
    material: language === 'ru' ? 'Текущий базовый материал' : language === 'nl' ? 'Huidig basismateriaal' : language === 'ro' ? 'Material de bază curent' : 'Current Base Material', 
    finish: language === 'ru' ? 'Текущее базовое покрытие' : language === 'nl' ? 'Huidige basiscoating' : language === 'ro' ? 'Finisaj de bază curent' : 'Current Base Coating', 
    inConcept: language === 'ru' ? 'В спецификации проекта' : safeString(t?.lookbook?.inConcept, language) || 'In Project Specification', 
    consultArchitect: safeString(t?.lookbook?.consultArchitect, language) || 'Consult an Architect' 
  };
  
  const specsLabels = { 
    category: safeString(t?.common?.category, language) || 'Category', 
    categoryValue: safeString(t?.categories?.[productCat], language) || productCat || '—', 
    material: safeString(t?.common?.material, language) || 'Material', 
    finish: safeString(t?.common?.finish, language) || 'Finish' 
  };

  const mainImageUrl = typeof product.images?.[0]?.url === 'string' 
    ? product.images[0].url 
    : (typeof product.images?.[0] === 'string' ? product.images[0] : '');

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: productName,
    description: productDesc,
    sku: safeString(product.sku, language),
    category: productCat,
    brand: { '@type': 'Brand', name: 'FEDYK Furniture' },
    manufacturer: { '@type': 'Organization', name: 'FEDYK Furniture' },
  };

  return (
    <div className="min-h-screen pt-8 text-left font-light select-none">
      <SEO 
        title={productName} 
        description={productDesc} 
        path={`products/${product.id}`} 
        image={mainImageUrl} 
        jsonLd={productSchema} 
      />
      
      <div className="container-custom py-6">
        <nav className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link to={`/${language}/products`} className="hover:text-foreground">
            {safeString(t?.nav?.products, language) || 'Products'}
          </Link>
          <span>/</span>
          <Link to={`/${language}/products?category=${productCat}`} className="hover:text-foreground">
            {safeString(t?.categories?.[productCat], language) || productCat || 'Category'}
          </Link>
          <span>/</span>
          <span className="text-foreground">{productName}</span>
        </nav>
      </div>

      <section className="container-custom pb-16" data-testid="product-layer-emotional">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start">
          <ProductGallery 
            images={Array.isArray(product.images) ? product.images : []} 
            alt={productName} 
          />
          
          <div className="space-y-12">
            <ProductInfo 
              product={cleanedProductForInfo} 
              language={language} 
              labels={infoLabels} 
              isInQuote={isInQuote} 
              isWishlisted={isWishlisted} 
              isB2BApproved={isB2BApproved} 
              onAddToQuote={handleAddToQuote} 
              onToggleWishlist={handleWishlist} 
              currentSelection={currentSelection}
            />

            {isB2BApproved && (
              <div className="border border-black p-6 bg-neutral-50 rounded-none space-y-5 print:hidden animate-in fade-in duration-300">
                <div>
                  <h4 className="text-xs uppercase font-medium tracking-widest text-black mb-1">
                    {safeString(t?.product?.b2bTitle, language) || 'B2B Trade Assets & Tools'}
                  </h4>
                  <p className="text-xs text-muted-foreground font-light">
                    {safeString(t?.product?.b2bSub, language) || 'Exclusive secure assets configured for verified design professionals.'}
                  </p>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {product.tech_sheet_url ? (
                    <a href={product.tech_sheet_url} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-3 p-3 bg-white border border-border hover:border-black hover:bg-black transition-all duration-300 min-w-0">
                      <FileText className="w-4 h-4 shrink-0 text-neutral-600 group-hover:text-white transition-colors" strokeWidth={1.5} />
                      <div className="truncate flex-1">
                        <p className="text-xs font-medium truncate text-gray-900 group-hover:text-white transition-colors">
                          {safeString(product.tech_sheet_filename || pageDict.techSheetFallback[language], language)}
                        </p>
                        <p className="text-[10px] text-muted-foreground group-hover:text-white/60 transition-colors">
                          {safeString(t?.product?.placeholder, language) || 'PDF Document'}
                        </p>
                      </div>
                    </a>
                  ) : (
                    <div className="p-3 bg-neutral-100 border border-dashed border-border text-center text-xs text-muted-foreground italic flex items-center justify-center">
                      {safeString(t?.product?.noPdf, language) || 'No PDF Document'}
                    </div>
                  )}

                  {product.model_3d_url ? (
                    <a href={product.model_3d_url} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-3 p-3 bg-white border border-border hover:border-black hover:bg-black transition-all duration-300 min-w-0">
                      <Box className="w-4 h-4 shrink-0 text-neutral-600 group-hover:text-white transition-colors" strokeWidth={1.5} />
                      <div className="truncate flex-1">
                        <p className="text-xs font-medium truncate text-gray-900 group-hover:text-white transition-colors">
                          {safeString(product.model_3d_filename || pageDict.model3dFallback[language], language)}
                        </p>
                        <p className="text-[10px] text-muted-foreground group-hover:text-white/60 transition-colors">
                          {safeString(pageDict.cadLabel[language], language) || 'CAD / 3D File'}
                        </p>
                      </div>
                    </a>
                  ) : (
                    <div className="p-3 bg-neutral-100 border border-dashed border-border text-center text-xs text-muted-foreground italic flex items-center justify-center">
                      {safeString(t?.product?.no3d, language) || 'No 3D Model File'}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button type="button" onClick={handleAddToProject} className="w-full h-11 bg-transparent border border-black text-black hover:bg-black hover:text-white text-[10px] uppercase tracking-widest font-medium transition-all rounded-none" data-testid="add-to-project-btn">
                    {language === 'ru' ? 'Добавить в спецификацию' : safeString(t?.product?.addToSpec, language) || 'Add to Project Spec'}
                  </button>
                  <button type="button" onClick={handleGeneratePDF} disabled={isGeneratingPdf} className="w-full h-11 bg-transparent border border-black text-black hover:bg-black hover:text-white text-[10px] uppercase tracking-widest font-medium transition-all rounded-none disabled:opacity-40 disabled:cursor-not-allowed">
                    {isGeneratingPdf ? (language === 'ru' ? 'Генерация...' : 'Generating...') : (safeString(t?.product?.generatePdf, language) || 'Generate PDF Specification')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {(product.dimensions || product.material || product.finish || (Array.isArray(product.materials) && product.materials.length > 0) || (Array.isArray(product.finishes) && product.finishes.length > 0)) && (
        <section className="border-t border-neutral-150 py-12" data-testid="product-layer-engineering">
          <div className="container-custom">
            <ProductSpecs 
              product={cleanedProductForSpecs} 
              labels={specsLabels} 
            />
          </div>
        </section>
      )}

      {cleanedRelatedProducts.length > 0 && (
        <div className="container-custom py-16">
          <RelatedProducts 
            products={cleanedRelatedProducts} 
            language={language} 
            categoryLabels={cleanedCategoryLabels} 
          />
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;
